// src/encounter/note-piece-blot.ts
//
// Registers the note PIECE with Quill (spec §6.2): an atomic inline Embed blot, so a pick-list,
// blank or chart link is one cursor stop, is deleted whole, and changes as one undo unit — typing
// can never bleed into it or split it. Its text is owned by its control (R2): the chip shows the
// piece's display and nothing typed in the note can edit it.
//
// The embed exists ONLY inside the React editor. `lib/note-pieces.ts` converts it to and from the
// wire form (a text run carrying the `cps-piece` attribute) at load and save, because flutter_quill
// 10.7.6 can break on an unknown embed but keeps an unknown attribute. The `cps-piece` attribute is
// registered here too, so any attributed run that does reach a Quill instance keeps its metadata
// instead of being silently stripped.
//
// Importing this module registers both (idempotent — Quill.register overwrites by name).

import { Quill } from 'react-quill-new';
import type EmbedType from 'quill/blots/embed';
import { PIECE_ATTR, PIECE_EMBED, chartScreenText, isPieceOpen, parsePiece, pieceDisplay, pieceLabel, type Piece } from './lib/note-pieces';

const Embed = Quill.import('blots/embed') as typeof EmbedType;
const Parchment = Quill.import('parchment');

const KIND_NAME: Record<Piece['t'], string> = { pick: 'pick-list', blank: 'blank', chart: 'chart value' };

// DS token classes. Open pieces read as "still to do" (the warning tint + a dashed rule); completed
// ones as quiet filled text.
const CHIP = 'cps-piece rounded-sm px-1 cursor-pointer';
const OPEN = 'border border-dashed border-status-warning bg-status-warning-bg text-status-amber-text';
const DONE = 'bg-status-info-bg text-text-primary';

/** What the chip shows: the display when complete; the label when open (the wire says
 *  `[not completed: <label>]`, the screen says what to do). */
export function chipText(p: Piece): string {
  if (!isPieceOpen(p)) return pieceDisplay(p);
  // An unresolved chart link says WHY on screen ("Allergies could not be loaded ▾").
  return p.t === 'chart' && p.snapshot ? `${chartScreenText(p)} ▾` : `${pieceLabel(p)} ▾`;
}

export function chipAriaLabel(p: Piece): string {
  const kind = KIND_NAME[p.t];
  if (isPieceOpen(p)) {
    const why = p.t === 'pick' ? 'not chosen' : p.t === 'chart' && p.snapshot ? chartScreenText(p) : 'not completed';
    return `${kind}, ${pieceLabel(p)}, ${why}`;
  }
  return `${kind}, ${pieceLabel(p)}: ${pieceDisplay(p)}`;
}

class PieceBlot extends Embed {
  static blotName = PIECE_EMBED;
  static tagName = 'span';
  static className = 'cps-piece';

  static create(value: unknown): HTMLElement {
    const node = super.create(value) as HTMLElement;
    const piece = parsePiece(value);
    if (!piece) return node;
    node.setAttribute('data-piece', JSON.stringify(piece));
    node.setAttribute('data-kind', piece.t);
    node.setAttribute('data-state', isPieceOpen(piece) ? 'open' : 'done');
    node.setAttribute('role', 'button');
    node.setAttribute('aria-label', chipAriaLabel(piece));
    node.className = `${CHIP} ${isPieceOpen(piece) ? OPEN : DONE}`;
    node.textContent = chipText(piece);
    return node;
  }

  static value(node: HTMLElement): Piece | undefined {
    return parsePiece(node.getAttribute('data-piece') ?? '');
  }
}

const PieceAttribute = new Parchment.Attributor(PIECE_ATTR, `data-${PIECE_ATTR}`, {
  scope: Parchment.Scope.INLINE_ATTRIBUTE,
});

Quill.register({ [`blots/${PIECE_EMBED}`]: PieceBlot, [`formats/${PIECE_ATTR}`]: PieceAttribute }, true);

export { PieceBlot };
